import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Icon, Input } from '@iq/react-components';
import config from '../../../config';
import { getFilteredActiveComponentId } from '../../../bundles/components';
import { getSources } from '../../../bundles/sources';
import {
  requestAnalytics,
  fetchExportAnalytics,
  getAnalyticsData,
  getAnalyticsTotal,
  getAnalyticsLoading,
} from '../../../bundles/analytics';
import { getActiveSite } from '../../../bundles/sites';
import { useDateSubscription, getTimezone } from '../../../bundles/application';
import { useDebounce, exportAnalyticsToFile } from '../../../utils';

import SelectPollingDateRange from '../../SelectPollingDateRange';
import { getColumnsForTableType } from './AnalyticsTableColumns';
import { SCROLL_THRESHOLD, SEARCHABLE_FIELDS, DEFAULT_SORT_BY_TABLE_TYPE } from './constants';
import { getSwitchgearCounterSourceContext } from './sourceUtils';
import { VirtualizedTable } from '../shared';

const parseDefaultSort = (defaultSort, tableType) => {
  if (defaultSort?.includes(':')) {
    const [column, direction] = defaultSort.split(':');
    return { sortBy: column, sortOrder: direction };
  }
  return {
    sortBy: DEFAULT_SORT_BY_TABLE_TYPE[tableType] || 'operation_count',
    sortOrder: 'DESC',
  };
};

// Shared by the on-screen table and the export so the exported file matches the visible sort order.
const sortAnalyticsRows = (rows, sortBy, sortOrder) => {
  if (!sortBy) return rows;
  return [...rows].toSorted((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue;
    }
    const comparison = String(aValue).localeCompare(String(bValue));
    return sortOrder === 'ASC' ? comparison : -comparison;
  });
};

const AnalyticsTablePanel = ({
  panelId,
  defaultSort,
  componentScope,
  tableType = 'switchgear-counter',
  additionalColumns = [],
  format,
  sourceId,
  sectionHeight,
  isFullScreen = false,
}) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const timezone = useSelector(getTimezone);
  const activeComponentId = useSelector(getFilteredActiveComponentId);
  const sources = useSelector(getSources);

  const rawData = useSelector((state) => getAnalyticsData(state, panelId));
  const total = useSelector((state) => getAnalyticsTotal(state, panelId));
  const isLoading = useSelector((state) => getAnalyticsLoading(state, panelId));

  const pollingDateRange = useDateSubscription(panelId);
  const { entityId, isEligible, resolvedSourceId } = useMemo(
    () =>
      getSwitchgearCounterSourceContext({
        sources,
        sourceId,
        isOnPremise: config.onPremise,
      }),
    [sources, sourceId]
  );

  const from = pollingDateRange?.startDate;
  const to = pollingDateRange?.endDate;

  const shouldFetch = useMemo(() => {
    if (!entityId || !isEligible) return false;

    const scope = componentScope?.selectedScope;
    if (!scope || scope === 'all') return true;
    if (!activeComponentId && (scope === 'active_all' || scope === 'active_children_all'))
      return true;
    if (!activeComponentId && (scope === 'active_none' || scope === 'active_children_none'))
      return false;
    return true;
  }, [componentScope, activeComponentId, entityId, isEligible]);

  const displayData = useMemo(() => (shouldFetch ? rawData : []), [shouldFetch, rawData]);
  const moreDataInApi = shouldFetch && displayData.length < total;

  const isNarrow = format === 'narrow';
  const shouldFillAvailableHeight =
    isFullScreen || ['half', 'two-thirds', 'full'].includes(sectionHeight);

  const columns = useMemo(
    () => getColumnsForTableType(tableType, displayData, format, additionalColumns, timezone),
    [tableType, displayData, format, additionalColumns, timezone]
  );

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const applyDebouncedSearch = useDebounce((text) => {
    setDebouncedSearchText(text);
  }, 200);

  const handleSearchChange = useCallback(
    (event) => {
      const value = event?.target ? event.target.value : event;
      setSearchText(value);
      applyDebouncedSearch(value);
    },
    [applyDebouncedSearch]
  );

  const { sortBy: initialSortBy, sortOrder: initialSortOrder } = useMemo(
    () => parseDefaultSort(defaultSort, tableType),
    [defaultSort, tableType]
  );

  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  useEffect(() => {
    setSortBy(initialSortBy);
    setSortOrder(initialSortOrder);
  }, [initialSortBy, initialSortOrder]);

  const handleSortChange = useCallback(
    (columnId) => {
      if (sortBy === columnId) {
        setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSortBy(columnId);
        setSortOrder(initialSortOrder);
      }
    },
    [sortBy, initialSortOrder]
  );

  const buildQuery = useCallback(
    (offset = 0) => ({
      org: site?.org,
      site: site?.id,
      tableType,
      sourceId: resolvedSourceId,
      entityId,
      offset,
      from,
      to,
    }),
    [site?.org, site?.id, tableType, resolvedSourceId, entityId, from, to]
  );

  const fetchData = useCallback(() => {
    if (!site?.id || !site?.org || !shouldFetch || !from || !to) return;
    dispatch(requestAnalytics(buildQuery(0), panelId));
  }, [dispatch, buildQuery, shouldFetch, site?.id, site?.org, panelId, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = useMemo(() => {
    if (!debouncedSearchText) return displayData;
    const searchLower = debouncedSearchText.toLowerCase();
    return displayData.filter((row) =>
      SEARCHABLE_FIELDS.some((field) => {
        const value = row[field];
        return typeof value === 'string' && value.toLowerCase().includes(searchLower);
      })
    );
  }, [displayData, debouncedSearchText]);

  const sortedData = useMemo(
    () => sortAnalyticsRows(filteredData, sortBy, sortOrder),
    [filteredData, sortBy, sortOrder]
  );

  const isExportDisabled = !entityId || !isEligible || filteredData.length === 0;

  let emptyMessage = 'No data available.';
  if (!entityId) {
    emptyMessage = 'No source configured.';
  } else if (!isEligible) {
    emptyMessage = 'Switchgear monitoring is disabled for the selected source.';
  }

  const handleRowsRendered = useCallback(
    ({ stopIndex }) => {
      if (!moreDataInApi || isLoading) return;

      const threshold = Math.min(SCROLL_THRESHOLD, displayData.length - 1);

      if (stopIndex >= displayData.length - threshold) {
        dispatch(requestAnalytics(buildQuery(displayData.length), panelId));
      }
    },
    [moreDataInApi, isLoading, displayData.length, dispatch, buildQuery, panelId]
  );

  const handleExport = useCallback(() => {
    if (!entityId || !isEligible || filteredData.length === 0) {
      return;
    }

    dispatch(
      fetchExportAnalytics({
        query: buildQuery(0),
        exportCallback: (allApiRows) => {
          const searchLower = debouncedSearchText.toLowerCase();
          const rowsMatchingSearch = searchLower
            ? allApiRows.filter((row) =>
                SEARCHABLE_FIELDS.some(
                  (field) =>
                    typeof row[field] === 'string' && row[field].toLowerCase().includes(searchLower)
                )
              )
            : allApiRows;

          exportAnalyticsToFile({
            siteName: site?.name || 'Unknown',
            exportType: 'xlsx',
            tableType,
            rows: sortAnalyticsRows(rowsMatchingSearch, sortBy, sortOrder),
            dateRange: { from, to },
            onExportCompleted: () => {
              console.log('Analytics export completed');
            },
          });
        },
        panelId,
      })
    );
  }, [
    dispatch,
    buildQuery,
    site?.name,
    tableType,
    from,
    to,
    panelId,
    debouncedSearchText,
    entityId,
    isEligible,
    filteredData.length,
    sortBy,
    sortOrder,
  ]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div
      className={`analytics-table-panel${isNarrow ? ' narrow' : ''}${isFullScreen ? ' full-screen' : ''}`}
    >
      <div className="analytics-table-panel-header">
        <Button
          icon={<Icon icon="he-download" />}
          activity="secondary"
          onClick={handleExport}
          disabled={isExportDisabled}
          tooltip="Export analytics"
        />
        <div className="analytics-table-panel-tools">
          {panelId && (
            <div className="analytics-table-panel-date-picker">
              <SelectPollingDateRange
                panelId={panelId}
                enableGlobalDates={false}
                onPoll={handleRefresh}
                // Section can be a fixed-height (1/2, 2/3) container that
                // clips overflow - see escapeOverflow in SelectPollingDateRange.
                escapeOverflow
              />
            </div>
          )}
          <div>
            <Input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>
      <VirtualizedTable
        columns={columns}
        data={sortedData}
        emptyMessage={emptyMessage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onRowsRendered={handleRowsRendered}
        isLoading={isLoading}
        loadMoreIndicator={isLoading && sortedData.length > 0}
        className="analytics-table"
        fillAvailableHeight={shouldFillAvailableHeight}
      />
    </div>
  );
};

export default AnalyticsTablePanel;
